import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import type { EditorQuestion } from './types'

type TfAnswerEditorProps = {
  question: Extract<EditorQuestion, { type: 'TrueFalse' }>
  onChange: (next: EditorQuestion) => void
}

/**
 * True / False answer editor: a two-button radio group where the chosen
 * button is the correct answer. Extracted verbatim from `QuizEditor` (E1).
 */
export function TfAnswerEditor({ question, onChange }: TfAnswerEditorProps) {
  const { t } = useTranslation('quiz')
  const groupName = useId()

  function setAnswer(value: boolean) {
    onChange({ ...question, correctAnswer: value })
  }

  return (
    <fieldset>
      <legend className="block text-sm font-semibold text-brand-900 mb-2">
        {t('correctAnswer')}
      </legend>
      <div className="grid grid-cols-2 gap-3">
        {[
          { value: true, label: t('trueLabel') },
          { value: false, label: t('falseLabel') },
        ].map(({ value, label }) => {
          const isCorrect = question.correctAnswer === value
          return (
            <label
              key={String(value)}
              className={
                'flex items-center justify-center gap-2 rounded-xl border ps-4 pe-4 py-3 cursor-pointer text-base font-semibold font-display transition-colors ' +
                (isCorrect
                  ? value
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                    : 'border-red-300 bg-red-50 text-red-700 ring-1 ring-red-200'
                  : 'border-brand-200 bg-white text-brand-700 hover:border-brand-300 hover:bg-brand-50/40')
              }
            >
              <input
                type="radio"
                name={groupName}
                checked={isCorrect}
                onChange={() => setAnswer(value)}
                className="sr-only"
              />
              <span>{label}</span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}
